import type { ContentItem } from '@kidshub/contracts';
import { type KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../i18n/use-locale.js';
import { Button, Icon } from '../ui/kit.js';

interface PlayNowSheetProps {
  /** What this child may watch: the only things the tablet would open anyway. */
  items: readonly Pick<ContentItem, 'id' | 'title' | 'posterUrl'>[];
  onPick: (item: Pick<ContentItem, 'id' | 'title' | 'posterUrl'>) => void;
  onClose: () => void;
}

/** Past this many a list wants a search box; below it, the box is in the way. */
const SEARCH_FROM = 8;

/**
 * "Open this on the tablet, now."
 *
 * A sheet rather than a page: it is a quick reach from the "now" card, made
 * while a child is waiting, and the card underneath is what tells the parent
 * whether it worked.
 */
export const PlayNowSheet = ({ items, onPick, onClose }: PlayNowSheetProps) => {
  const { t } = useTranslation();
  const locale = useLocale();
  const titleId = useId();
  const searchRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    // Back to whatever opened the sheet when it closes, as a dialog should.
    const opener = document.activeElement;
    searchRef.current?.focus();
    return () => {
      if (opener instanceof HTMLElement) {
        opener.focus();
      }
    };
  }, []);

  const closeOnEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
  };

  // Matched the way the library page matches, so the same word finds the same
  // videos in both places.
  const needle = query.trim().toLocaleLowerCase(locale);
  const shown =
    needle === ''
      ? items
      : items.filter((item) => item.title.toLocaleLowerCase(locale).includes(needle));

  return (
    <>
      <div className="scrim open" aria-hidden="true" onClick={onClose} />
      <div
        className="sheet open"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={closeOnEscape}
      >
        <div className="sheet-grip" aria-hidden="true" />
        <h2 className="t-h2" id={titleId}>
          {t('live.playNowTitle')}
        </h2>
        <p className="t-meta">{t('live.playNowMeta')}</p>

        <div className="sheet-body">
          {items.length < SEARCH_FROM ? null : (
            <input
              ref={searchRef}
              className="input"
              type="search"
              value={query}
              placeholder={t('live.playNowSearch')}
              aria-label={t('live.playNowSearch')}
              onChange={(event) => setQuery(event.target.value)}
            />
          )}

          {shown.length === 0 ? (
            <p className="t-meta">{t('live.playNowEmpty')}</p>
          ) : (
            <ul className="play-now-list">
              {shown.map((item) => (
                <li key={item.id}>
                  <button type="button" className="play-now-item" onClick={() => onPick(item)}>
                    <span className="poster play-now-poster">
                      {item.posterUrl === null ? (
                        <Icon name="play" small />
                      ) : (
                        <img src={item.posterUrl} alt="" />
                      )}
                    </span>
                    <span className="play-now-title">{item.title}</span>
                    <Icon name="play" small />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="sheet-actions">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </>
  );
};
